import React, { useEffect, useState, useRef } from 'react';
import { BASKET_LEAD_CAPTURE_FORM_ID, BRAND, LEAD_FORM_ID } from '../../constants/main';
import { SC } from '../../styles/theme';
import formService from '../../services/digital/form.service';
import Spinner from '../Spinner';
import { captureException } from '@utils/sentry.utils';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { FEATURES_LIST } from '../../context/featureSwitch';
import { UserService } from '../../services';
import { getSessionIdCookie } from '@utils/Session.utils';
import { useGTM } from '@hooks/useGTM';
import { GtmData } from '../../types/gtm';
import { useWidgetCompatibility } from '@hooks/useWidgetCompatibility';
import { JQueryDependency } from '@utils/externalDependencies/dependencies';
import AssetManager from '@utils/externalDependencies/AssetManager';
import { IConfigurableCar } from '../../interfaces/Car';

export interface IDigitalFormProps extends SC {
    formId: number | string;
    formData?: { [key: string]: number | string };
    currentCarDetails: IConfigurableCar;
}

//@ts-ignore
const brandTemplate = { AP: 'peugeot', AC: 'citroen', DS: 'ds', OV: 'vauxhall' }[BRAND];

let loadAssets = 0;
const defaultForm = '';
const lang = 'en-gb';
const model = '';

const DigitalForm: React.FC<IDigitalFormProps> = ({
    formId,
    formData = {},
    className,
    currentCarDetails,
    ...props
}) => {
    const containerRef = useRef<HTMLDivElement>();
    const [isLoading, setIsLoading] = useState(true);

    const { saveWindowProperty, restoreWindowProperty } = useWidgetCompatibility();

    const isAllowedFormLeadsToCRMStock = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_ENABLE_FORM_LEADS_TO_CRM_STOCK
    );

    const isAllowedFormLeadsToCRMMTO = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_ENABLE_FORM_LEADS_TO_CRM_MTO
    );
    const isCollectConsentEnabledMTO = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_ENABLE_COLLECT_CONSENT_FORM_LEADS_TO_CRM_MTO
    );
    const isCollectConsentEnabledStock = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_ENABLE_COLLECT_CONSENT_FORM_LEADS_TO_CRM_STOCK
    );

    const { isStockJourney } = useCarStockJourneyCheck();
    const { pushToDataLayer, getBodyStyleLabel } = useGTM();

    const sendDataToCRM = (crmData: any) => {
        UserService.sendDataToCRM(getSessionIdCookie(), crmData).catch((err) => {
            console.log(err.message || 'Problem with CRM udpate');
        });
    };

    const getFormCheckedCheckboxesTexts = () => {
        const checkBoxes = document.getElementsByClassName('checkbox-field') as unknown as NodeListOf<HTMLInputElement>;
        const checkboxes = [];
        for (let i = 0; i < checkBoxes.length; i++) {
            if (checkBoxes[i].checked) {
                checkboxes.push(checkBoxes[i].value);
            }
        }

        return checkboxes.length ? checkboxes.join(';') : '';
    };

    const getFormElementValue = (selector: string, isId?: boolean) => {
        const getSingleCheckboxValue = (): boolean => {
            const checkboxes = document.getElementsByName(selector) as NodeListOf<HTMLInputElement>;

            return checkboxes.length === 1 && checkboxes[0].checked;
        };

        switch (selector) {
            case 'comment':
                return (
                    document
                        .getElementsByClassName(selector)?.[0]
                        ?.getElementsByTagName('textarea')?.[0] as HTMLTextAreaElement
                )?.value;
            case 'civility':
                const selectField = document.querySelector('select[name="civility"]') as HTMLInputElement;
                if (selectField) {
                    return selectField.value;
                }
                return (document.querySelector('input[name="civility"]:checked') as HTMLInputElement)?.value;
            case 'preferredContactTypeEmail[]':
            case 'preferredContactTypeSMS[]':
            case 'preferredContactTypePhone[]':
                return getSingleCheckboxValue();
            default:
                return isId
                    ? document.getElementById(selector)?.getElementsByTagName('input')?.[0]?.value
                    : document.getElementsByClassName(selector)?.[0]?.getElementsByTagName('input')?.[0].value;
        }
    };

    const collectDataForCRM: any = (formData: any) => {
        const { lcdv16, type } = formData;

        const data = {
            salutation: getFormElementValue('civility'),
            firstName: getFormElementValue('firstName', true),
            lastName: getFormElementValue('lastName', true),
            mobilePersonalTelephone: getFormElementValue('phone', true),
            postalCode: getFormElementValue('postCode', true),
            email: getFormElementValue('emailCust'),
            customerComments: getFormElementValue('comment'),
            lcdvInterest: lcdv16,
            checkBox1Meaning: 'MkgBrandMail',
            checkBox1Value: getFormElementValue('preferredContactTypeEmail[]'),
            checkBox2Meaning: 'MkgBrandSMS',
            checkBox2Value: getFormElementValue('preferredContactTypeSMS[]'),
            checkBox3Meaning: 'MkgBrandPhone',
            checkBox3Value: getFormElementValue('preferredContactTypePhone[]'),
            checkedCheckboxesTexts: getFormCheckedCheckboxesTexts(),
        };

        //custom form data validation
        let noError = true;
        for (const [key, value] of Object.entries(data)) {
            // validate only text inputs that are present - their value is gonna be a string not undefined/null
            if (typeof value !== 'string') continue;

            if (
                !value.length &&
                ![
                    'customerComments',
                    'checkedCheckboxesTexts',
                    type === 'LEAD_COLLECT_CONSENT_FORM' && 'mobilePersonalTelephone',
                ].includes(key)
            ) {
                noError = false;
            }
        }

        if (document.getElementsByClassName('emailErr').length) noError = false;

        return noError ? data : noError;
    };

    function hookSubmit(btnTxt: string) {
        formService
            .sendForm({ dealId: formData.dealId })
            .then((res) => {
                if (
                    (isStockJourney && !isAllowedFormLeadsToCRMStock && formId === LEAD_FORM_ID) ||
                    (!isStockJourney && !isAllowedFormLeadsToCRMMTO && formId === LEAD_FORM_ID) ||
                    (isStockJourney && !isCollectConsentEnabledStock && formId === BASKET_LEAD_CAPTURE_FORM_ID) ||
                    (!isStockJourney && !isCollectConsentEnabledMTO && formId === BASKET_LEAD_CAPTURE_FORM_ID)
                )
                    return;
                const { status } = res;

                if (status === 200) {
                    const crmData = collectDataForCRM(formData);
                    if (crmData) {
                        const { type, mopId } = formData;
                        sendDataToCRM({ ...crmData, type, mopId });
                    } else {
                        console.error('There is still validation error so CRM data is not sent.');
                    }
                }
            })
            .catch((e) => {
                console.error('Siebel update was not correct.', e);
            });
    }

    useEffect(() => {
        setIsLoading(true);
        const params = {
            form_id: formId,
            model,
            template: brandTemplate,
            default_form: defaultForm,
            lang,
            loadAssets,
            ...formData,
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                (containerRef.current.querySelector('.submit-button') as HTMLButtonElement).click();
            }
        };
        Promise.all([AssetManager.instance.addScripts([JQueryDependency]), formService.getForm(params)])
            .then(([_, res]) => {
                const { data } = res;
                if (data && data.html) {
                    const { email } = formData;
                    saveWindowProperty('jQuery');
                    restoreWindowProperty('$');

                    const html: string = data.html.replace(/<link.*>/g, '').replace(/<style.*>[\s\S]*<\/style>/g, '');
                    const container = containerRef.current;

                    (window as any).$(container).html(html);

                    const submitButton = container.querySelector('.submit-button');
                    const formField = container.querySelectorAll('.form-field');

                    // In BO form builder there can just info box set instead of form
                    if (submitButton) {
                        container.querySelector('.submit-button').setAttribute('href', '#');
                        container.addEventListener('keydown', handleKeyDown);
                        submitButton.addEventListener('click', () => hookSubmit(submitButton.textContent));
                    }

                    if (Boolean(formField.length)) {
                        for (let i = 0; i <= formField.length; i++) {
                            if (typeof formField[i] === 'undefined') continue;
                            const input = formField[i].querySelector('input');
                            const textarea = formField[i].querySelector('textarea');
                            const h4 = formField[i].querySelector('h4');
                            const emailField = formField[i].querySelector("[name='emailCust']");

                            if (emailField && email) {
                                (emailField as HTMLInputElement).value = email as string;
                            }

                            if (textarea) {
                                textarea.addEventListener('blur', () => {
                                    pushToDataLayer({
                                        eventCategory: 'SolRetail::Basket',
                                        eventAction: `Select::Field::CommercialOffer::${getBodyStyleLabel(
                                            currentCarDetails
                                        )}`,
                                        eventLabel: h4?.innerText,
                                    } as GtmData);
                                });
                            }
                            if (input && input.type !== 'hidden') {
                                input.addEventListener(
                                    ['checkbox', 'radio'].includes(input.type) ? 'click' : 'blur',
                                    () => {
                                        pushToDataLayer({
                                            eventCategory: 'SolRetail::Basket',
                                            eventAction: `Select::Field::CommercialOffer::${getBodyStyleLabel(
                                                currentCarDetails
                                            )}`,
                                            eventLabel: h4?.innerText,
                                        } as GtmData);
                                    }
                                );
                            }
                        }
                    }

                    setIsLoading(false);
                } else {
                    console.error('Digital form data empty', res);
                }
            })
            .catch((e) => {
                captureException(e);
                console.error("Couldn't load digital form", e);
            });
        loadAssets++;

        return () => {
            containerRef.current.removeEventListener('keydown', handleKeyDown);
        };
    }, [formId, ...Object.values(formData)]);
    return (
        <React.Fragment>
            {isLoading ? <Spinner /> : null}
            <div
                ref={containerRef}
                className={`ap-forms ${className}`}
                data-form-id={formId}
                data-model={model}
                data-template={brandTemplate}
                data-lang={lang}
                data-default-form={defaultForm}
                {...props}
            />
        </React.Fragment>
    );
};

export default DigitalForm;
